import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';

// project import
import { OpenInNew } from 'components/icons';
import useAuth from 'hooks/useAuth';
import { isMenuItemVisible } from 'menu-items/menuVisibility';
import { activeItem } from 'store/reducers/menu';

// types
import { LinkTarget, NavItemType } from 'types/menu';
import { RootStateProps } from 'types/root';
import { cn } from 'utils/cn';
import { matchesNavPathname } from './navItemMatch';
import { newTabIconClass, nestedRowClass, popupRowClass, topLevelIconClass, topLevelRowClass } from './navStyles';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

interface Props {
    item: NavItemType;
    level: number;
    isPopup: boolean;
}

function NavItem({ item, level, isPopup }: Props) {
    const dispatch = useDispatch();
    const { drawerOpen, openItem } = useSelector((state: RootStateProps) => state.menu);
    const { user } = useAuth();
    const { pathname } = useLocation();

    const isDisabled = !isMenuItemVisible(item, user);
    const itemTarget: LinkTarget = item.target ?? '_self';
    const isSelected = openItem.some((id) => id === item.id);

    // active menu item on page load
    useEffect(() => {
        if (matchesNavPathname(pathname, item.url, item.altActiveUrls)) {
            dispatch(activeItem({ openItem: [item.id] }));
        }
        // eslint-disable-next-line
    }, [pathname]);

    const maybePreventNavigation = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (pathname === item.url?.split('?')[0]) {
            e.preventDefault();
        }
    };

    // A top-level item shares the drawer with the `NavCollapse` rows around it, so it takes their
    // geometry. A nested item is its own inset pill in the open drawer, and a full-width row in the
    // flyout the rail opens.
    const isTopLevel = level === 1;
    const showLabel = drawerOpen || !isTopLevel;

    let rowClass: string;
    if (isPopup) rowClass = popupRowClass(isSelected);
    else if (isTopLevel) rowClass = topLevelRowClass(drawerOpen, isSelected);
    else rowClass = nestedRowClass(isSelected);

    const Icon = item.icon;
    const content = (
        <>
            {Icon && (
                <span className={topLevelIconClass(drawerOpen)}>
                    <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} />
                </span>
            )}
            {showLabel ? (
                <span
                    className={cn(
                        'text-primary min-w-0 flex-1',
                        isPopup ? 'text-sm/5.5' : 'my-1 font-semibold',
                        !isPopup && (isTopLevel ? 'text-sm/5.5' : 'text-xs/5.5')
                    )}
                    style={item.style}
                >
                    {item.title}
                </span>
            ) : (
                // Same gap as NavCollapse: no label in the collapsed rail, so no accessible name.
                <span className="sr-only">{item.title}</span>
            )}
            {showLabel && itemTarget === '_blank' && <OpenInNew className={newTabIconClass} />}
        </>
    );

    const className = cn(
        rowClass,
        isTopLevel && drawerOpen && 'pl-6',
        item.disabled && 'pointer-events-none opacity-38'
    );
    const disabledProps = item.disabled ? { 'aria-disabled': true, tabIndex: -1 } : {};

    if (isDisabled) return null;
    return (
        <li>
            {item.external ? (
                <a href={item.url} target={itemTarget} className={className} {...disabledProps}>
                    {content}
                </a>
            ) : (
                <Link
                    to={item.url!}
                    target={itemTarget}
                    onClick={maybePreventNavigation}
                    className={className}
                    {...disabledProps}
                >
                    {content}
                </Link>
            )}
        </li>
    );
}

export default NavItem;
